// Resellers (doc 03 §2). NCI admin only.
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { api, type Channel, type Me, type Page, type Reseller } from "../api";
import { Badge, Card, Empty, ErrorNote, Field, Modal, PageHeader, Spinner, useAction } from "../ui";

export default function Resellers({ me }: { me: Me }) {
  const [editing, setEditing] = useState<Reseller | "new" | null>(null);
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery<Page<Reseller>>({
    queryKey: ["resellers"],
    queryFn: () => api.get("/v1/resellers"),
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ["resellers"] });

  return (
    <>
      <PageHeader
        title="Resellers"
        subtitle="Every Outpost belongs to exactly one reseller. The channel catalog controls what they may sell."
        actions={me.role === "nci_admin" && <button className="btn-primary" onClick={() => setEditing("new")}>New reseller</button>}
      />

      <Card padded={false}>
        {isLoading ? <Spinner /> : error ? <div className="p-4"><ErrorNote error={error} /></div> : !data?.items.length ? (
          <Empty>No resellers yet.</Empty>
        ) : (
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>Name</th><th>Status</th><th>Contact</th><th>Billing</th>
                  <th>Sites</th><th>Outposts</th><th>Users</th><th>Catalog</th><th />
                </tr>
              </thead>
              <tbody>
                {data.items.map((reseller) => (
                  <tr key={reseller.id}>
                    <td className="font-medium">{reseller.name}</td>
                    <td><Badge tone={reseller.status === "active" ? "ok" : "warn"}>{reseller.status}</Badge></td>
                    <td className="text-ink-300">{reseller.contact_email ?? "—"}</td>
                    <td className="text-ink-300">{reseller.billing_email ?? "—"}</td>
                    <td className="tabular-nums">{reseller.site_count ?? 0}</td>
                    <td className="tabular-nums">{reseller.endpoint_count ?? 0}</td>
                    <td className="tabular-nums">
                      {reseller.user_count ? (
                        reseller.user_count
                      ) : (
                        // A reseller with no account cannot see anything they are
                        // paying for, which is worth saying out loud.
                        <span className="text-state-warn">none yet</span>
                      )}
                    </td>
                    <td className="text-ink-400">
                      {reseller.channel_catalog.length ? `${reseller.channel_catalog.length} channels` : "all active"}
                    </td>
                    <td className="text-right">
                      {me.role === "nci_admin" && <button className="btn-ghost" onClick={() => setEditing(reseller)}>edit</button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {editing && (
        <ResellerModal
          reseller={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onDone={() => { setEditing(null); refresh(); }}
        />
      )}
    </>
  );
}

function ResellerModal({ reseller, onClose, onDone }: { reseller: Reseller | null; onClose: () => void; onDone: () => void }) {
  const [form, setForm] = useState({
    name: reseller?.name ?? "",
    contact_email: reseller?.contact_email ?? "",
    billing_email: reseller?.billing_email ?? "",
    phone: reseller?.phone ?? "",
    status: reseller?.status ?? "active",
    require_totp: reseller?.require_totp ?? false,
  });
  const [catalog, setCatalog] = useState<number[]>(reseller?.channel_catalog ?? []);
  const { run, busy } = useAction();

  const { data: channels } = useQuery<Page<Channel>>({
    queryKey: ["channels"],
    queryFn: () => api.get("/v1/channels?status=active"),
  });

  return (
    <Modal
      title={reseller ? `Edit ${reseller.name}` : "New reseller"}
      onClose={onClose}
      wide
      footer={
        <>
          <button className="btn-secondary" onClick={onClose}>Cancel</button>
          <button
            className="btn-primary"
            disabled={busy || !form.name}
            onClick={() => run(async () => {
              const body = { ...form, channel_catalog: catalog,
                contact_email: form.contact_email || null, billing_email: form.billing_email || null, phone: form.phone || null };
              if (reseller) await api.patch(`/v1/resellers/${reseller.id}`, body);
              else await api.post("/v1/resellers", body);
              onDone();
            }, reseller ? "Reseller updated" : "Reseller created")}
          >
            {busy ? "Saving…" : "Save"}
          </button>
        </>
      }
    >
      <div className="grid gap-4 md:grid-cols-2">
        <Field label="Name"><input className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
        <Field label="Status">
          <select className="input" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
            <option value="active">active</option><option value="suspended">suspended</option><option value="closed">closed</option>
          </select>
        </Field>
        <Field label="Operations contact"><input className="input" type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} /></Field>
        <Field label="Billing contact" hint="Where finalised invoices are emailed.">
          <input className="input" type="email" value={form.billing_email} onChange={(e) => setForm({ ...form, billing_email: e.target.value })} />
        </Field>
        <Field label="Phone"><input className="input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></Field>
      </div>

      <div className="mt-5 rounded-md border border-ink-600 p-3">
        <label className="flex items-start gap-3 text-sm">
          <input
            type="checkbox"
            className="mt-0.5 accent-brand"
            checked={form.require_totp}
            onChange={(e) => setForm({ ...form, require_totp: e.target.checked })}
          />
          <span>
            <span className="font-semibold text-ink-100">Require two-factor for this reseller</span>
            <span className="mt-0.5 block text-xs text-ink-400">
              Mandatory for NCI staff, and an admin decision per reseller (doc 03 §2). Turning it on takes
              effect at each of their users&apos; next sign-in — they are walked through enrolment before
              they can do anything else. Nobody is locked out.
            </span>
          </span>
        </label>
      </div>

      <div className="mt-5">
        <div className="label">Channel catalog</div>
        <p className="mb-2 text-xs text-ink-400">
          Leave everything unchecked to allow the whole active lineup. Checking any channel restricts this
          reseller to exactly what is checked.
        </p>
        <div className="max-h-56 overflow-y-auto rounded border border-ink-600 p-2">
          <div className="grid grid-cols-2 gap-1 md:grid-cols-3">
            {channels?.items.map((channel) => (
              <label key={channel.id} className="flex items-center gap-2 rounded px-2 py-1 text-sm hover:bg-ink-700">
                <input
                  type="checkbox"
                  className="accent-brand"
                  checked={catalog.includes(channel.id)}
                  onChange={(e) =>
                    setCatalog(e.target.checked ? [...catalog, channel.id] : catalog.filter((id) => id !== channel.id))
                  }
                />
                <span className="tabular-nums text-ink-400">{channel.number}</span>
                <span className="truncate">{channel.name}</span>
              </label>
            ))}
          </div>
        </div>
      </div>
    </Modal>
  );
}
